/**
 * The level-2 disclosure body of a search hit (search-result-disclosure 03),
 * extracted so the scanner can show the same preview instead of a lookalike.
 *
 * It renders identity, placements, facts, deadlines, the photo strip, contents
 * and the placement controls. The expand/collapse chrome and the level-3
 * "Preview" action stay with `ResultRow`; the scanner owns its own action bar.
 */

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AddIcon, RemoveIcon } from "../../shared/components/icons";
import {
  CONTAINER_DEADLINE_VOCABULARY,
  DeadlineChips,
  ITEM_DEADLINE_VOCABULARY,
} from "../../shared/components/Deadlines";
import { useT } from "../../shared/i18n";
import { IdentityLine } from "../../shared/components/IdentityLine";
import type { SearchDisplayState } from "./facets";
import { HitContents, HitFacts } from "./HitBits";
import { HitPhotoStrip } from "./ResultMedia";
import { identityNameOf, identityUuidOf, type SearchHit } from "./searchHits";

export function HitLevelTwo({
  hit,
  display,
  onOpenPlacement,
  onOpenContainer,
  quantity,
  canWrite = true,
  onQuantityChange,
  identity,
}: {
  hit: SearchHit;
  display: SearchDisplayState;
  /** Open one of a multi-placed item's hosts (search rows only). */
  onOpenPlacement?: (containerId: string) => void;
  /** Link to the row's container; the item list only passes it for one placement. */
  onOpenContainer?: () => void;
  /** Current placement quantity (optimistic value wins over the hit's mirror). */
  quantity?: number;
  /** False for a read-only share: the link stays, the stepper hides. */
  canWrite?: boolean;
  onQuantityChange?: (next: number) => void;
  /** Override the identity line (the item list shows the item, not its host). */
  identity?: { uuid: string; name: string };
}) {
  const t = useT();
  const isItem = hit.result.type === "item";
  const vocabulary = isItem ? ITEM_DEADLINE_VOCABULARY : CONTAINER_DEADLINE_VOCABULARY;
  const identityValue = identity ?? { uuid: identityUuidOf(hit), name: identityNameOf(hit) };
  const showIdentity = identityValue.uuid !== "";
  const placementCount = isItem ? hit.placements.length : 0;
  const showPlacementList = placementCount > 1 && onOpenPlacement !== undefined;
  const showContainerControls = isItem && !hit.noPlacement && onOpenContainer !== undefined;
  const currentQuantity = quantity ?? hit.quantity;
  const showStepper =
    showContainerControls &&
    canWrite &&
    currentQuantity !== undefined &&
    onQuantityChange !== undefined;

  return (
    <Stack spacing={1}>
      {showIdentity && <IdentityLine uuid={identityValue.uuid} name={identityValue.name} />}
      {showPlacementList && (
        <Box data-testid="hit-placements">
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {t("searchPlacements")}
          </Typography>
          <Stack spacing={0.25}>
            {hit.placements.map((placement) => (
              <Button
                key={placement.containerId}
                size="small"
                onClick={() => onOpenPlacement?.(placement.containerId)}
                data-testid={`hit-placement-${placement.containerId}`}
                sx={{ justifyContent: "flex-start", textTransform: "none", px: 0.5 }}
              >
                {placement.path}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      <HitFacts hit={hit} />
      <DeadlineChips properties={hit.properties} vocabulary={vocabulary} />
      {display.previews && <HitPhotoStrip photos={hit.photos} />}
      <HitContents contents={hit.contents} />
      {showContainerControls && (
        <Stack direction="row" spacing={1} alignItems="center" data-testid="hit-placement-controls">
          <Button
            size="small"
            onClick={onOpenContainer}
            aria-label={`${t("openContainer")} ${identityNameOf(hit)}`}
            data-testid="hit-container-link"
            sx={{ textTransform: "none" }}
          >
            {identityNameOf(hit)}
          </Button>
          {showStepper && (
            <Stack direction="row" spacing={0} alignItems="center">
              <IconButton
                size="small"
                aria-label={t("decrementQuantity")}
                data-testid="hit-quantity-dec"
                onClick={() => onQuantityChange?.(currentQuantity - 1)}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                data-testid="hit-quantity"
                sx={{ minWidth: 24, textAlign: "center" }}
              >
                {currentQuantity}
              </Typography>
              <IconButton
                size="small"
                aria-label={t("incrementQuantity")}
                data-testid="hit-quantity-inc"
                onClick={() => onQuantityChange?.(currentQuantity + 1)}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
